(() => {
  const instances = new WeakMap();
  function init(root) {
    if (instances.has(root) || root.dataset.onReady === 'true') return;
    const desktop = root.querySelector('.on-desktop-nav');
    const dialog = root.querySelector('.on-mobile-dialog');
    const toggle = root.querySelector('.on-menu-toggle');
    const row = root.querySelector('.on-header-row');
    if (!desktop || !dialog || !toggle || !row) return;
    root.dataset.onReady = 'true';
    const controller = new AbortController();
    const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: controller.signal });
    const groups = [...desktop.querySelectorAll('[data-on-group]')];
    const mobileGroups = [...dialog.querySelectorAll('[data-on-group]')];
    const desktopMedia = window.matchMedia('(min-width:1200px)');
    let previousOverflow = null;
    let resizing = false;
    let previousScroll = Math.max(0, window.scrollY);
    let frame = 0;
    let keyboardFocus = false;
    const lock = () => {
      if (previousOverflow === null) previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    };
    const unlock = () => {
      if (previousOverflow !== null) document.body.style.overflow = previousOverflow;
      previousOverflow = null;
    };
    const setHidden = hidden => {
      root.dataset.onHidden = String(hidden);
      root.inert = hidden;
      document.body.style.setProperty('--sticky-header-offset', hidden ? '0px' : `${row.offsetHeight}px`);
    };
    const syncState = () => {
      const menuOpen = dialog.open || groups.some(group => group.open);
      root.dataset.onMenuOpen = String(menuOpen);
      root.dataset.onAtTop = String(window.scrollY <= 2);
      if (menuOpen || window.scrollY <= 2 || keyboardFocus || root.dataset.onEditorActive === 'true') setHidden(false);
    };
    const position = () => {
      const height = row.offsetHeight;
      const viewport = window.visualViewport;
      const viewportHeight = viewport ? viewport.height : window.innerHeight;
      root.style.setProperty('--on-panel-top', `${height}px`);
      root.style.setProperty('--on-panel-height', `${Math.max(0, viewportHeight - height)}px`);
      document.body.style.setProperty('--header-height', `${height}px`);
      document.body.style.setProperty('--header-group-height', root.dataset.overlayHome === 'true' ? '0px' : `${height}px`);
    };
    const closeDesktop = (restoreFocus = false) => {
      const opened = groups.find(group => group.open);
      groups.forEach(group => { group.open = false; });
      if (!dialog.open) unlock();
      syncState();
      if (restoreFocus && opened) opened.querySelector('summary').focus();
    };
    const selectProduct = (group, button) => {
      group.querySelectorAll('[data-on-pick]').forEach(pick => {
        pick.dataset.active = String(pick === button);
        pick.querySelector('[data-on-show]').setAttribute('aria-pressed', String(pick === button));
      });
      group.querySelectorAll('[data-on-preview]').forEach(preview => { preview.hidden = preview.dataset.onPreview !== button.dataset.onPick; });
    };
    listen(root, 'click', event => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-on-category-link]')) closeDesktop();
      const show = event.target.closest('[data-on-show]');
      if (show) {
        const pick = show.closest('[data-on-pick]');
        if (pick) selectProduct(pick.closest('[data-on-group]'), pick);
      }
      if (event.target.closest('.on-panel-close')) closeDesktop(true);
      const variant = event.target.closest('[data-on-variant]');
      if (!variant) return;
      const preview = variant.closest('[data-on-preview]');
      preview.querySelectorAll('[data-on-variant]').forEach(option => option.setAttribute('aria-pressed', String(option === variant)));
      const image = preview.querySelector('.on-preview-image');
      if (image && variant.dataset.image) {
        if (variant.dataset.srcset) image.srcset = variant.dataset.srcset;
        else image.removeAttribute('srcset');
        image.src = variant.dataset.image;
        preview.querySelector('.on-preview-photo').dataset.onFit = variant.dataset.fit;
      }
      preview.querySelector('[data-on-price]').textContent = variant.dataset.price;
      const compare = preview.querySelector('[data-on-compare]');
      compare.textContent = variant.dataset.compare;
      compare.hidden = !variant.dataset.compare;
      preview.querySelector('[data-on-choice]').textContent = variant.dataset.title;
      preview.querySelectorAll('[data-on-product-link]').forEach(link => { link.href = variant.dataset.url; });
      preview.querySelector('[data-on-status]').hidden = variant.dataset.available === 'true';
      preview.querySelector('.on-preview-live').textContent = `${variant.dataset.title} · ${variant.dataset.price}`;
    });
    listen(root, 'pointerover', event => {
      if (!(event.target instanceof Element) || !window.matchMedia('(hover:hover) and (pointer:fine)').matches) return;
      const pick = event.target.closest('[data-on-pick]');
      if (pick && desktop.contains(pick)) selectProduct(pick.closest('[data-on-group]'), pick);
    });
    listen(root, 'focusin', event => {
      if (!(event.target instanceof Element)) return;
      keyboardFocus = event.target.matches(':focus-visible');
      setHidden(false);
      const pick = event.target.closest('[data-on-pick]');
      if (pick) selectProduct(pick.closest('[data-on-group]'), pick);
    });
    listen(root, 'focusout', event => {
      if (!root.contains(event.relatedTarget)) keyboardFocus = false;
    });
    groups.forEach(group => {
      const summary = group.querySelector('summary');
      listen(summary, 'click', event => {
        if (!desktopMedia.matches || event.detail === 0) return;
        if (event.target instanceof Element && event.target.closest('[data-on-category-link]')) return;
        event.preventDefault();
        group.open = true;
      });
      listen(summary, 'pointerenter', event => {
        if (event.pointerType === 'mouse' && desktopMedia.matches) group.open = true;
      });
      listen(group, 'toggle', () => {
        if (group.open && desktopMedia.matches) {
          groups.forEach(other => { if (other !== group) other.open = false; });
          position();
          lock();
        } else if (!groups.some(other => other.open) && !dialog.open) unlock();
        syncState();
      });
    });
    listen(document, 'click', event => {
      if (event.target instanceof Node && !desktop.contains(event.target)) closeDesktop();
    });
    listen(document, 'keydown', event => {
      if (event.key === 'Escape' && !dialog.open) closeDesktop(true);
    });
    listen(toggle, 'click', () => {
      closeDesktop();
      dialog.showModal();
      lock();
      toggle.setAttribute('aria-expanded', 'true');
      syncState();
      dialog.querySelector('.on-menu-close').focus();
    });
    listen(dialog.querySelector('.on-menu-close'), 'click', () => dialog.close());
    listen(dialog, 'close', () => {
      toggle.setAttribute('aria-expanded', 'false');
      unlock();
      syncState();
      if (!resizing) toggle.focus();
      previousScroll = Math.max(0, window.scrollY);
    });
    mobileGroups.forEach(group => {
      listen(group, 'toggle', () => {
        if (group.open) mobileGroups.forEach(other => { if (other !== group) other.open = false; });
      });
      const products = [...group.querySelectorAll('[data-on-product-disclosure]')];
      products.forEach(product => listen(product, 'toggle', () => {
        if (product.open) products.forEach(other => { if (other !== product) other.open = false; });
      }));
    });
    listen(window, 'scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const y = Math.max(0, Math.min(window.scrollY, document.documentElement.scrollHeight - window.innerHeight));
        const delta = y - previousScroll;
        syncState();
        const pinned = dialog.open || groups.some(group => group.open) || keyboardFocus || root.dataset.onEditorActive === 'true' || !!document.querySelector('dialog[open]');
        if (!pinned && y > 2 && delta !== 0) setHidden(delta > 0);
        previousScroll = y;
      });
    }, { passive: true });
    listen(window, 'resize', () => {
      resizing = true;
      if (desktopMedia.matches && dialog.open) dialog.close();
      if (!desktopMedia.matches) closeDesktop();
      position();
      syncState();
      resizing = false;
    }, { passive: true });
    if (window.visualViewport) listen(window.visualViewport, 'resize', position, { passive: true });
    const observer = new ResizeObserver(position);
    observer.observe(row);
    listen(document, 'shopify:section:select', event => {
      if (event.target.contains(root)) { root.dataset.onEditorActive = 'true'; syncState(); }
    });
    listen(document, 'shopify:section:deselect', event => {
      if (event.target.contains(root)) root.dataset.onEditorActive = 'false';
    });
    const dispose = () => {
      if (dialog.open) dialog.close();
      closeDesktop();
      unlock();
      controller.abort();
      observer.disconnect();
      cancelAnimationFrame(frame);
      root.inert = false;
      delete root.dataset.onReady;
      instances.delete(root);
    };
    instances.set(root, dispose);
    listen(document, 'shopify:section:unload', event => { if (event.target.contains(root)) dispose(); });
    position();
    syncState();
  }
  document.querySelectorAll('.on-header').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('.on-header').forEach(init));
})();




